import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { refreshMyDataFromGestao } from "@/lib/user.functions";
import { formatCpf } from "@/lib/cpf";
import { GESTAO_URL } from "@/lib/sensitive-field-errors";
import { accessBlockCopy } from "@/lib/member-access";
import { SensitiveFieldsNotice } from "./sensitive-fields-notice";
import { Logo } from "./logo";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { AlertTriangle, LogOut, LifeBuoy, ExternalLink, RefreshCw } from "lucide-react";
import { SiteFooter } from "./site-footer";
import { toast } from "sonner";

type Props = {
  missing: string[];
  fullName?: string | null;
  cpf?: string | null;
  /** Motivo do bloqueio informado pela sincronização com a Gestão. */
  blockReason?: string | null;
  /** Detalhe adicional registrado na sincronização. */
  blockDetail?: string | null;
};

/**
 * Bloqueia o acesso enquanto houver dados obrigatórios pendentes no cadastro.
 * Os dados NÃO são editáveis aqui: a correção é feita na Gestão de cadastros e
 * chega a esta plataforma pela sincronização automática.
 */
export function CompleteRegistrationScreen({
  missing,
  fullName,
  cpf,
  blockReason,
  blockDetail,
}: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const refreshFn = useServerFn(refreshMyDataFromGestao);

  const refresh = useMutation({
    mutationFn: () => refreshFn(),
    onSuccess: async (res: any) => {
      await queryClient.invalidateQueries({ queryKey: ["access-status"] });
      if (res?.found === false) toast.error(res.message ?? "Cadastro não localizado na Gestão.");
      else toast.success("Dados atualizados conforme a Gestão de cadastros.");
    },
    onError: (e: any) => toast.error(e?.message ?? "Não foi possível verificar seus dados agora."),
  });

  const signOut = async () => {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/auth", replace: true });
  };

  const copy = accessBlockCopy(blockReason, missing);
  const showData = !blockReason || blockReason === "dados_incompletos";

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border/60 bg-background/95">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 md:px-6">
          <Logo size={36} />
          <Button variant="ghost" size="sm" onClick={signOut}>
            <LogOut className="mr-2 h-4 w-4" /> Sair
          </Button>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-4 py-12">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold/15">
          <AlertTriangle className="h-7 w-7 text-gold-foreground" />
        </div>
        <h1 className="mt-5 text-center font-serif text-2xl font-semibold text-primary sm:text-3xl">
          {copy.title}
        </h1>
        <p className="mt-3 text-center text-sm text-muted-foreground sm:text-base">
          {copy.description}
        </p>
        {blockDetail ? (
          <p className="mt-2 text-center text-xs text-muted-foreground">{blockDetail}</p>
        ) : null}

        <div className="mt-8 space-y-4 rounded-xl border border-border bg-card p-5">
          {showData ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="cr-name">Nome completo</Label>
                <Input
                  id="cr-name"
                  value={fullName ?? ""}
                  readOnly
                  placeholder="—"
                  className="cursor-not-allowed bg-muted/50"
                  aria-describedby="cr-dados-bloqueados"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cr-cpf">CPF</Label>
                <Input
                  id="cr-cpf"
                  value={formatCpf(cpf ?? "")}
                  readOnly
                  placeholder="—"
                  className="cursor-not-allowed bg-muted/50"
                  aria-describedby="cr-dados-bloqueados"
                />
              </div>

              <SensitiveFieldsNotice id="cr-dados-bloqueados" />
            </>
          ) : null}

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button asChild className="flex-1">
              <a href={GESTAO_URL} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="mr-2 h-4 w-4" />{" "}
                {showData ? "Completar na Gestão de cadastros" : "Abrir a Gestão de cadastros"}
              </a>
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => refresh.mutate()}
              disabled={refresh.isPending}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${refresh.isPending ? "animate-spin" : ""}`} />
              {refresh.isPending ? "Verificando..." : "Já atualizei, verificar"}
            </Button>
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          <Button asChild variant="outline" size="sm">
            <Link to="/suporte">
              <LifeBuoy className="mr-2 h-4 w-4" /> Suporte & FAQ
            </Link>
          </Button>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}
